import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import Tours from './components/Tours';
import HowItWorks from './components/HowItWorks';
import Guide from './components/Guide';
import Reviews from './components/Reviews';
import FAQ from './components/FAQ';
import Gallery from './components/Gallery';
import Contact from './components/Contact';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import BookingModal from './components/BookingModal';
import PaymentStatus from './components/PaymentStatus';
import ErrorBoundary from './components/ErrorBoundary';
// Admin pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCalendar from './pages/admin/AdminCalendar';
import AdminBookings from './pages/admin/AdminBookings';
import AdminDiscounts from './pages/admin/AdminDiscounts';
import AdminReferrals from './pages/admin/AdminReferrals';
import AdminReviews from './pages/admin/AdminReviews';
import AdminTours from './pages/admin/AdminTours';
import AdminMedia from './pages/admin/AdminMedia';
import AdminGallery from './pages/admin/AdminGallery';
import AdminTukTuks from './pages/admin/AdminTukTuks';
import AdminTips from './pages/admin/AdminTips';
import AdminEmail from './pages/admin/AdminEmail';
import ReviewPage from './pages/ReviewPage';
import axios from 'axios';
import './App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Component that detects language by IP
const LanguageDetector = ({ children }) => {
  const { setLanguage } = useLanguage();
  const [detected, setDetected] = useState(false);

  useEffect(() => {
    const detectLanguage = async () => {
      // Check if user already has a preference
      const savedLang = localStorage.getItem('language');
      if (savedLang) {
        setDetected(true);
        return;
      }

      try {
        const response = await axios.get(`${API}/detect-language`);
        const detectedLang = response.data.language;
        if (['es', 'en', 'fr'].includes(detectedLang)) {
          setLanguage(detectedLang);
        }
      } catch (error) {
        console.log('Could not detect language, using default');
      }
      setDetected(true);
    };

    detectLanguage();
  }, [setLanguage]);

  if (!detected) {
    return null; // Or a loading spinner
  }

  return children;
};

const HomePage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [tours, setTours] = useState([]);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const response = await axios.get(`${API}/tours`);
        setTours(response.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      }
    };
    fetchTours();
  }, []);

  const handleBookClick = () => {
    setSelectedTour(null);
    setIsBookingOpen(true);
  };

  const handleBookTour = (tour) => {
    setSelectedTour(tour);
    setIsBookingOpen(true);
  };

  return (
    <LanguageDetector>
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="home-page">
        <Navigation onBookClick={handleBookClick} />
        <Hero onBookClick={handleBookClick} />
        <Tours onBookTour={handleBookTour} />
        <HowItWorks />
        <Guide />
        <Reviews />
        <FAQ />
        <Gallery />
        <Contact />
        <Footer />
        <WhatsAppButton />
        
        <BookingModal
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
          selectedTour={selectedTour}
          tours={tours}
        />
      </div>
    </LanguageDetector>
  );
};

const BookingSuccessPage = () => {
  return <PaymentStatus />;
};

const BookingCancelPage = () => {
  useEffect(() => {
    setTimeout(() => {
      window.location.href = '/';
    }, 3000);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <h2 className="font-display text-2xl font-bold mb-4">
          Reserva Cancelada
        </h2>
        <p className="text-[#57534E]">
          Tu reserva ha sido cancelada. Serás redirigido a la página principal.
        </p>
      </div>
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <Toaster position="top-center" richColors />
        <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/booking/success" element={<BookingSuccessPage />} />
            <Route path="/booking/cancel" element={<BookingCancelPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="calendar" element={<AdminCalendar />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="tours" element={<AdminTours />} />
              <Route path="tuktuks" element={<AdminTukTuks />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="discounts" element={<AdminDiscounts />} />
              <Route path="referrals" element={<AdminReferrals />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="tips" element={<AdminTips />} />
              <Route path="emails" element={<AdminEmail />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </ErrorBoundary>
      </div>
    </LanguageProvider>
  );
}

export default App;
